import React, { useEffect, useMemo, useState } from "react";
import useIsMobile from "../../hooks/useIsMobile";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getSnapTradeAccounts, getSnapTradeHoldings } from "../../api/snaptrade";
import Button from "../../components/Button";
import FeatureGate from "../../components/gating/FeatureGate";

const MANUAL_ASSETS_STORAGE_KEY = "finai-manual-assets-v1";
const PERFORMANCE_SETTINGS_STORAGE_KEY = "finai-performance-settings-v1";

const BENCHMARKS = [
  { id: "sp500", name: "S&P 500", annualReturn: 10.0 },
  { id: "nifty50", name: "Nifty 50", annualReturn: 11.0 },
  { id: "china", name: "China Large-Cap Index", annualReturn: 7.0 },
  { id: "nyc_real_estate", name: "New York Real Estate", annualReturn: 5.5 },
  { id: "us_real_estate", name: "US Real Estate", annualReturn: 4.8 },
  { id: "bond_index", name: "Investment Grade Bonds", annualReturn: 4.5 },
  { id: "cash_rate", name: "Cash / T-Bill Rate", annualReturn: 3.8 },
  { id: "gold", name: "Gold", annualReturn: 6.0 },
  { id: "btc", name: "Bitcoin", annualReturn: 18.0 },
  { id: "industry", name: "Industry Standard", annualReturn: 7.0 },
];

const DEFAULT_BENCHMARK_BY_TYPE = {
  Stocks: "sp500",
  Stock: "sp500",
  "Mutual Funds": "sp500",
  "Mutual Fund": "sp500",
  ETF: "sp500",
  "Real Estate / Land": "us_real_estate",
  Bonds: "bond_index",
  Bond: "bond_index",
  "Cash / Savings": "cash_rate",
  Cash: "cash_rate",
  "Gold / Commodities": "gold",
  Crypto: "btc",
  "Private Business": "industry",
  Other: "industry",
};

const CURRENCY_TO_USD = {
  USD: 1,
  INR: 1 / 83.3,
  EUR: 1.09,
  GBP: 1.27,
  CAD: 0.74,
  AUD: 0.65,
  JPY: 1 / 151.0,
  HKD: 0.128,
};

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const pct = (value) => `${value >= 0 ? "+" : ""}${(value || 0).toFixed(2)}%`;

const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toUsd = (value, currencyCode) => value * (CURRENCY_TO_USD[currencyCode] || 1);

const loadJson = (key, fallback) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch {
    return fallback;
  }
};

const getHoldingYears = (purchaseDate) => {
  if (!purchaseDate) return 1;
  const start = new Date(purchaseDate);
  if (Number.isNaN(start.getTime())) return 1;
  const years = (Date.now() - start.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  return Math.max(years, 0.1);
};

const annualizedReturn = (currentValue, costBasis, holdingYears) => {
  if (currentValue <= 0 || costBasis <= 0 || holdingYears <= 0) return 0;
  return (Math.pow(currentValue / costBasis, 1 / holdingYears) - 1) * 100;
};

const getBenchmark = (asset, settings) => {
  const selectedId =
    settings[asset.id]?.benchmarkId ||
    DEFAULT_BENCHMARK_BY_TYPE[asset.assetType] ||
    DEFAULT_BENCHMARK_BY_TYPE[asset.type] ||
    "industry";
  return BENCHMARKS.find((benchmark) => benchmark.id === selectedId) || BENCHMARKS[BENCHMARKS.length - 1];
};

const normalizeLinkedHolding = (holding) => {
  const marketValue = numberValue(holding.marketValue);
  const costBasis = numberValue(holding.costBasis);
  const unrealizedPL = numberValue(holding.unrealizedPL);
  const currencyCode = holding.currency || "USD";

  return {
    id: `linked-${holding.accountId || holding.account || "account"}-${holding.symbol}`,
    source: "Linked Account",
    name: holding.name || holding.symbol,
    symbol: holding.symbol || "ASSET",
    assetType: holding.assetType || "Stocks",
    type: holding.assetType || "Stocks",
    marketValue,
    costBasis,
    unrealizedPL,
    currency: currencyCode,
    valueUSD: toUsd(marketValue, currencyCode),
    costBasisUSD: toUsd(costBasis, currencyCode),
    holdingYears: 1,
  };
};

const normalizeAccountBalance = (account) => {
  const marketValue = numberValue(account.balance);
  const currencyCode = account.currency || "USD";
  const brokerage = account.brokerage || account.name || "Linked Account";

  return {
    id: `account-cash-${account.id || brokerage}`,
    source: "Linked Account",
    name: `${brokerage} Available Balance`,
    symbol: `${brokerage.slice(0, 4).toUpperCase()} CASH`,
    assetType: "Cash / Savings",
    type: "Cash / Savings",
    marketValue,
    costBasis: marketValue,
    unrealizedPL: 0,
    currency: currencyCode,
    valueUSD: toUsd(marketValue, currencyCode),
    costBasisUSD: toUsd(marketValue, currencyCode),
    holdingYears: 1,
  };
};

const normalizeManualAsset = (asset) => {
  const derivedLandValue = numberValue(asset.area) * numberValue(asset.localPricePerUnit);
  const marketValue = numberValue(asset.currentValue) || derivedLandValue;
  const costBasis = numberValue(asset.costBasis);
  const currencyCode = asset.currency || "USD";

  return {
    id: asset.id,
    source: "Manual",
    name: asset.name || asset.symbol || "Manual Asset",
    symbol: asset.symbol || asset.name?.slice(0, 6).toUpperCase() || "ASSET",
    assetType: asset.type || "Other",
    type: asset.type || "Other",
    marketValue,
    costBasis,
    unrealizedPL: marketValue - costBasis,
    currency: currencyCode,
    valueUSD: toUsd(marketValue, currencyCode),
    costBasisUSD: toUsd(costBasis, currencyCode),
    holdingYears: getHoldingYears(asset.purchaseDate),
    purchaseDate: asset.purchaseDate || "",
    location: asset.location || "",
    area: numberValue(asset.area),
    areaUnit: asset.areaUnit || "",
    localPricePerUnit: numberValue(asset.localPricePerUnit),
  };
};

const ReturnComparisonTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg text-sm dark:border-gray-700 dark:bg-gray-950">
      <p className="font-semibold text-gray-900 dark:text-white">{label}</p>
      <p className="mt-2 text-indigo-600">Actual: {pct(row.actual)}</p>
      <p className="text-emerald-600">
        Selected Target ({row.targetName}): {pct(row.benchmark)}
      </p>
      <p className="text-amber-600">Your Expected Return: {pct(row.target)}</p>
    </div>
  );
};

const Performance = () => {
  const isMobile = useIsMobile();
  const [linkedHoldings, setLinkedHoldings] = useState([]);
  const [linkedCashHoldings, setLinkedCashHoldings] = useState([]);
  const [manualAssets, setManualAssets] = useState(() => loadJson(MANUAL_ASSETS_STORAGE_KEY, []));
  const [settings, setSettings] = useState(() => loadJson(PERFORMANCE_SETTINGS_STORAGE_KEY, {}));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [timeframe, setTimeframe] = useState("1Y");
  const [taxCountry, setTaxCountry] = useState("United States");

  useEffect(() => {
    const fetchHoldings = async () => {
      try {
        setLoading(true);
        const [holdingsResponse, accountsResponse] = await Promise.allSettled([
          getSnapTradeHoldings(),
          getSnapTradeAccounts(),
        ]);

        if (holdingsResponse.status === "fulfilled") {
          setLinkedHoldings((holdingsResponse.value.data.holdings || []).map(normalizeLinkedHolding));
        } else {
          setLinkedHoldings([]);
        }

        if (accountsResponse.status === "fulfilled") {
          setLinkedCashHoldings(
            (accountsResponse.value.data.accounts || [])
              .map(normalizeAccountBalance)
              .filter((asset) => Math.abs(asset.marketValue) > 0)
          );
        } else {
          setLinkedCashHoldings([]);
        }

        setManualAssets(loadJson(MANUAL_ASSETS_STORAGE_KEY, []));
        setError("");
      } catch (err) {
        console.error("Performance holdings fetch failed:", err);
        setManualAssets(loadJson(MANUAL_ASSETS_STORAGE_KEY, []));
        setError("Linked holdings could not be loaded. Showing manual assets only.");
      } finally {
        setLoading(false);
      }
    };

    fetchHoldings();
  }, []);

  useEffect(() => {
    localStorage.setItem(PERFORMANCE_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  const assets = useMemo(
    () => [...linkedHoldings, ...linkedCashHoldings, ...manualAssets.map(normalizeManualAsset)],
    [linkedCashHoldings, linkedHoldings, manualAssets]
  );

  const analyzedAssets = useMemo(() => {
    return assets.map((asset) => {
      const benchmark = getBenchmark(asset, settings);
      const targetReturn =
        settings[asset.id]?.targetReturn === "" || settings[asset.id]?.targetReturn === undefined
          ? benchmark.annualReturn
          : numberValue(settings[asset.id]?.targetReturn);
      const actualReturn = annualizedReturn(asset.valueUSD, asset.costBasisUSD, asset.holdingYears);

      return {
        ...asset,
        benchmark,
        targetReturn,
        actualReturn,
        gainUSD: asset.valueUSD - asset.costBasisUSD,
        alpha: actualReturn - benchmark.annualReturn,
        targetGap: actualReturn - targetReturn,
      };
    });
  }, [assets, settings]);

  const summary = useMemo(() => {
    const totalValue = analyzedAssets.reduce((sum, asset) => sum + asset.valueUSD, 0);
    const totalCost = analyzedAssets.reduce((sum, asset) => sum + asset.costBasisUSD, 0);
    const totalGain = totalValue - totalCost;
    const weightedReturn = totalValue
      ? analyzedAssets.reduce((sum, asset) => sum + asset.actualReturn * (asset.valueUSD / totalValue), 0)
      : 0;
    const weightedBenchmark = totalValue
      ? analyzedAssets.reduce((sum, asset) => sum + asset.benchmark.annualReturn * (asset.valueUSD / totalValue), 0)
      : 0;
    const aheadCount = analyzedAssets.filter((asset) => asset.alpha >= 0).length;
    const lossHarvest = analyzedAssets.reduce((sum, asset) => sum + Math.min(asset.gainUSD, 0), 0);

    return {
      totalValue,
      totalCost,
      totalGain,
      totalReturn: totalCost > 0 ? (totalGain / totalCost) * 100 : 0,
      weightedReturn,
      weightedBenchmark,
      alpha: weightedReturn - weightedBenchmark,
      aheadCount,
      lossHarvest,
      taxableGain: analyzedAssets.reduce((sum, asset) => sum + Math.max(asset.gainUSD, 0), 0),
    };
  }, [analyzedAssets]);

  const chartData = useMemo(() => {
    const monthsByFrame = { "1M": 1, "3M": 3, "6M": 6, "1Y": 12, Max: 36 };
    const months = monthsByFrame[timeframe];
    const data = [];
    const startValue = summary.totalCost || summary.totalValue || 100000;
    const portfolioMonthly = Math.pow(1 + summary.weightedReturn / 100, 1 / 12) - 1;
    const benchmarkMonthly = Math.pow(1 + summary.weightedBenchmark / 100, 1 / 12) - 1;

    for (let index = 0; index <= months; index += 1) {
      const date = new Date();
      date.setMonth(date.getMonth() - (months - index));
      data.push({
        label: date.toLocaleDateString("en-US", { month: "short", year: months > 12 ? "2-digit" : undefined }),
        portfolio: Math.round(startValue * Math.pow(1 + portfolioMonthly, index)),
        benchmark: Math.round(startValue * Math.pow(1 + benchmarkMonthly, index)),
      });
    }

    return data;
  }, [summary.totalCost, summary.totalValue, summary.weightedBenchmark, summary.weightedReturn, timeframe]);

  const comparisonData = analyzedAssets.map((asset) => ({
    name: asset.symbol,
    actual: Number(asset.actualReturn.toFixed(2)),
    benchmark: Number(asset.benchmark.annualReturn.toFixed(2)),
    target: Number(asset.targetReturn.toFixed(2)),
    targetName: asset.benchmark.name,
  }));

  const updateSetting = (assetId, field, value) => {
    setSettings((current) => ({
      ...current,
      [assetId]: {
        benchmarkId: current[assetId]?.benchmarkId || "",
        targetReturn: current[assetId]?.targetReturn ?? "",
        ...current[assetId],
        [field]: value,
      },
    }));
  };

  const topIssue = useMemo(() => {
    if (analyzedAssets.length === 0) return "Add assets in Holdings to see performance analysis.";
    const worst = [...analyzedAssets].sort((a, b) => a.alpha - b.alpha)[0];
    if (!worst || worst.alpha >= 0) return "Your assets are currently beating or matching their selected comparison targets.";
    return `${worst.symbol} is trailing ${worst.benchmark.name} by ${Math.abs(worst.alpha).toFixed(2)} percentage points annualized.`;
  }, [analyzedAssets]);

  const taxSuggestions = useMemo(() => {
    if (analyzedAssets.length === 0) {
      return ["Add holdings first so FinAI can estimate filing considerations."];
    }

    const suggestions = [];
    if (summary.taxableGain > 0) {
      suggestions.push(`${currency.format(summary.taxableGain)} of unrealized gains may need planning before sales.`);
    }
    if (summary.lossHarvest < 0) {
      suggestions.push(`${currency.format(Math.abs(summary.lossHarvest))} of losses may be useful for tax-loss harvesting review.`);
    }
    if (analyzedAssets.some((asset) => asset.assetType === "Real Estate / Land")) {
      suggestions.push("For land or property, keep purchase documents, improvement records, property tax receipts, and sale expense records.");
    }
    if (analyzedAssets.some((asset) => asset.assetType === "Crypto")) {
      suggestions.push("For crypto, export complete exchange and wallet transaction history before filing.");
    }
    suggestions.push(`${taxCountry}: confirm holding period rules, account type treatment, and local filing requirements with a tax professional.`);
    return suggestions;
  }, [analyzedAssets, summary.lossHarvest, summary.taxableGain, taxCountry]);

  return (
    <div className="space-y-8 animate-fadeIn pt-4">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">Performance</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-2 max-w-3xl">
            Analysis only. Assets come from Holdings, and linked-account assets come through Linked Accounts.
          </p>
        </div>
        <Button href="/portfolio/holdings" className="self-start lg:self-auto">
          Manage Holdings
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          ["Portfolio Value", currency.format(summary.totalValue)],
          ["Total Return", pct(summary.totalReturn)],
          ["Annualized Return", pct(summary.weightedReturn)],
          ["Target Difference", pct(summary.alpha)],
        ].map(([label, value]) => (
          <div
            key={label}
            className="p-5 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
            <p
              className={`mt-2 text-2xl sm:text-3xl font-bold ${
                (label.includes("Difference") || label.includes("Return")) && String(value).startsWith("-") ? "text-red-500" : ""
              }`}
            >
              {value}
            </p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 p-6 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h3 className="text-xl font-bold">Portfolio vs Comparison Target</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Each asset uses its own comparison target, blended by asset value.
              </p>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              {["1M", "3M", "6M", "1Y", "Max"].map((option) => (
                <button
                  key={option}
                  onClick={() => setTimeframe(option)}
                  className={`px-3 py-1.5 rounded-lg transition ${
                    timeframe === option
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="h-80 flex items-center justify-center text-gray-500">Loading performance data...</div>
          ) : analyzedAssets.length === 0 ? (
            <div className="h-80 flex items-center justify-center text-center text-gray-500">
              Add assets in Holdings or link accounts first.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={isMobile ? 240 : 320}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(value) => `$${Math.round(value / 1000)}k`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value) => currency.format(value)} />
                <Legend />
                <Line type="monotone" dataKey="portfolio" name="Your Portfolio" stroke="#4F46E5" strokeWidth={3} dot={false} />
                <Line type="monotone" dataKey="benchmark" name="Comparison Target" stroke="#10B981" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-6 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white">
          <h3 className="text-xl font-bold">What Matters</h3>
          <div className="mt-4 space-y-4">
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Main Signal</p>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{topIssue}</p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Coverage</p>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                {summary.aheadCount} of {analyzedAssets.length} assets are beating their selected comparison target.
              </p>
            </div>
            <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700">
              <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Next Action</p>
              <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">
                Set the comparison target and expected return for each asset below. Do not add assets here.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 p-6 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white">
          <h3 className="text-xl font-bold mb-4">Actual Return vs Comparison Target</h3>
          {comparisonData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-gray-500">No assets to compare.</div>
          ) : (
            <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#CBD5E1" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(value) => `${value}%`} tick={{ fontSize: 12 }} />
                <Tooltip content={<ReturnComparisonTooltip />} />
                <Legend />
                <Bar dataKey="actual" name="Actual" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                <Bar dataKey="benchmark" name="Selected Target" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="target" name="Your Expected Return" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="p-6 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white">
          <h3 className="text-xl font-bold">Tax Filing Suggestions</h3>
          <select
            value={taxCountry}
            onChange={(event) => setTaxCountry(event.target.value)}
            className="mt-4 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-950 outline-none"
          >
            <option>United States</option>
            <option>India</option>
            <option>United Kingdom</option>
            <option>Canada</option>
            <option>Other Jurisdiction</option>
          </select>
          <div className="mt-4 space-y-3">
            {taxSuggestions.map((text) => (
              <div key={text} className="pb-3 border-b last:border-b-0 border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-white/70 dark:bg-[#1a2332]/60 border border-gray-200 dark:border-white/10 shadow-sm text-gray-900 dark:text-white">
        <div className="mb-4">
          <h3 className="text-xl font-bold">Comparison Target Settings</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Choose what each existing holding should be compared against, plus your personal expected return.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="py-3 text-left">Asset</th>
                <th className="py-3 text-left hidden md:table-cell">Source</th>
                <th className="py-3 text-right hidden lg:table-cell">Value</th>
                <th className="py-3 text-left">Comparison Target</th>
                <th className="py-3 text-right hidden sm:table-cell">Expected %</th>
                <th className="py-3 text-right">Actual</th>
                <th className="py-3 text-right">Difference</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {analyzedAssets.map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50 dark:hover:bg-gray-900/60">
                  <td className="py-3 pr-3 min-w-[140px] sm:min-w-[180px]">
                    <p className="font-semibold">{asset.symbol}</p>
                    <p className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-none">{asset.name}</p>
                  </td>
                  <td className="py-3 pr-3 text-gray-600 dark:text-gray-400 hidden md:table-cell">{asset.source}</td>
                  <td className="py-3 text-right font-semibold hidden lg:table-cell">{currency.format(asset.valueUSD)}</td>
                  <td className="py-3 min-w-[160px] sm:min-w-[210px]">
                    <select
                      value={settings[asset.id]?.benchmarkId || ""}
                      onChange={(event) => updateSetting(asset.id, "benchmarkId", event.target.value)}
                      className="w-full bg-transparent outline-none"
                    >
                      <option value="">Default: {getBenchmark(asset, settings).name}</option>
                      {BENCHMARKS.map((benchmark) => (
                        <option key={benchmark.id} value={benchmark.id}>
                          {benchmark.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 text-right hidden sm:table-cell">
                    <input
                      value={settings[asset.id]?.targetReturn ?? ""}
                      onChange={(event) => updateSetting(asset.id, "targetReturn", event.target.value)}
                      placeholder={`${asset.benchmark.annualReturn}`}
                      inputMode="decimal"
                      className="w-20 text-right bg-transparent border-b border-gray-300 dark:border-gray-700 outline-none"
                    />
                  </td>
                  <td className={`py-3 text-right font-semibold ${asset.actualReturn >= 0 ? "text-green-600" : "text-red-500"}`}>
                    {pct(asset.actualReturn)}
                  </td>
                  <td className={`py-3 text-right font-semibold ${asset.alpha >= 0 ? "text-green-600" : "text-red-500"}`}>
                    {pct(asset.alpha)}
                  </td>
                </tr>
              ))}
              {analyzedAssets.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-8 text-center text-gray-500">
                    Add assets in Holdings to configure comparison targets.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PerformanceGated = () => (
  <FeatureGate feature="performance">
    <Performance />
  </FeatureGate>
);

export default PerformanceGated;
